// src/components/billing/PatientInvoices.jsx
import React, { useEffect, useState, useContext } from "react";
import { Row, Col, Form, Table, Button, Badge, Card, Modal, InputGroup } from "react-bootstrap";
import { Receipt } from "react-bootstrap-icons";
import { formatDisplayDate, exportToExcel } from '../../utils/formatter';
import { showToast } from "../reusables/CommonFunction";
import { ToastContainer } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import PrintInvoiceModal from "./PrintInvoiceModal";
import BusyLoaderOverlay from "../reusables/BusyLoaderOverlay";
import CustomPagination from "../reusables/CustomPagination";
import axiosInstance from "../../utils/axios";

const PatientInvoices = () => {
    let { userDetails } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("")
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [fetchingPrint, setFetchingPrint] = useState(false);
    const [isPatientModalOpen, setPatientModalOpen] = useState(false);
    const [searchTriggered, setSearchTriggered] = useState(false);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [patientDetails, setPatientDetails] = useState([])
    const [printInvoiceData, setPrintInvoiceData] = useState(null);
    const [patientInvoicesDetails, setPatientInvoicesDetails] = useState([])
    const [filters, setFilters] = useState({
        query: "",
        dateFrom: "",
        dateTo: "",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const sourceTypes = { "personal": "1st Consultation", "followup": "Follow-up", "dental": "Dental Treatment", "bulk_dental": "Bulk Dental Treatment" };
    const salutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' };
    const isDentist = userDetails?.doctor_type === "dentist";


    const onPressSearchPatient = (oEvent) => {
        oEvent.preventDefault();
        if (!searchQuery.trim()) {
            showToast("error", "Please enter a patient name or case number to search.");
            return;
        }

        setLoading(true);
        axiosInstance
            .get(`/patients/`, {
                params: {
                    q: searchQuery,
                    clinicId: userDetails.clinic_id
                }
            })
            .then(response => {
                if (response.status === 200) {
                    setPatientDetails(response.data.data)
                }
            })
            .catch(error => {
                showToast("error", "Failed to fetch patient list");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const onPressConfirmPatient = (oEvent, patient) => {
        setFilters((prev) => ({ ...prev, query: `${salutation[patient.salutation]} ${patient.patient_name}` }));
        setSelectedPatientId(patient.patient_id);
        setSearchQuery(`${salutation[patient.salutation]} ${patient.patient_name}`);
        setPatientModalOpen(false);
        setShowDateFilter(true);
        setPatientInvoicesDetails([]);
        setCurrentPage(1);
        setTotalCount(0);
    }

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const onPressSearchInvoices = (oEvent) => {
        oEvent.preventDefault();
        const hasFilters = filters.query || filters.dateFrom || filters.dateTo;

        if (!hasFilters) {
            showToast("error", "Please enter search criteria before searching.");
            return;
        }

        setSearchTriggered(true);
        setCurrentPage(1); // Reset to the first page
        getInvoiceDetails(1);
    }

    const getInvoiceDetails = async (pageNumber) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/billing/", {
                params: {
                    clinicId: userDetails.clinic_id,
                    page: pageNumber,
                    patientId: selectedPatientId,
                    date_from: filters.dateFrom,
                    date_to: filters.dateTo
                }
            });

            setPatientInvoicesDetails(response.data.data);
            setTotalCount(response.data.count);
        } catch (err) {
            showToast("error", "Failed to fetch invoice details");
        } finally {
            setLoading(false);
        }
    };

    const onPressReset = () => {
        setFilters({ query: "", dateFrom: "", dateTo: "" });
        setShowDateFilter(false);
        setSelectedPatientId(null);
        setCurrentPage(1);
        setTotalCount(0);
        setPatientInvoicesDetails([]);
    };

    const onPressOpenModal = async (pInvoice) => {
        setSelectedInvoice(pInvoice);
        setFetchingPrint(true);
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/billing/${pInvoice.invoice_id}/`);
            setPrintInvoiceData(response.data);
            setShowPrintModal(true);
        } catch (err) {
            showToast("error", "Failed to fetch invoice details");
        } finally {
            setLoading(false);
            setFetchingPrint(false);
        }
    };

    const onPressCloseModal = () => {
        setShowPrintModal(false);
        setPrintInvoiceData(null);
    };

    useEffect(() => {
        if (searchTriggered) {
            getInvoiceDetails(currentPage);
        }
    }, [currentPage]);

    return (
        <>
            <Row className='ml-3 mr-3 mb-3'>
                <div className='col-md-12'>
                    <Card className="mb-4">
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Receipt size={20} className="text-muted" />
                                <h5 className="mb-0">Patient Invoices</h5>
                            </div>

                            <Row className='mb-3'>
                                <Col xs={6}>
                                    <Form.Control
                                        readOnly
                                        type="text"
                                        placeholder="Click here to select a patient"
                                        value={filters.query}
                                        onClick={() => { setPatientModalOpen(true); setPatientDetails([]); setSearchQuery("") }}
                                    />

                                    <Modal size="lg" backdrop="static" keyboard={false} centered show={isPatientModalOpen} onHide={() => { setPatientModalOpen(false) }} >
                                        <Modal.Header closeButton>
                                            <Modal.Title>Search Patient</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <Row className="ml-3 mr-3 mb-3">
                                                <div className='col-md-12'>
                                                    <InputGroup className="mb-3 pl-0" >
                                                        <Form.Control
                                                            type="text"
                                                            name="searchQuery"
                                                            autoFocus
                                                            onChange={(oEvent) => setSearchQuery(oEvent.target.value)}
                                                            onKeyDown={(oEvent) => oEvent.key === "Enter" && onPressSearchPatient(oEvent)}
                                                            placeholder="Type patient name or case number and press Enter..."
                                                        />
                                                        <Button type="submit" variant="outline-secondary" onClick={onPressSearchPatient}>
                                                            Search
                                                        </Button>
                                                    </InputGroup>

                                                    {/* If the count is 0 then hide the table */}
                                                    {patientDetails.length > 0 ?
                                                        <Table bordered hover responsive>
                                                            <thead light="true">
                                                                <tr>
                                                                    <th style={{ width: "2rem" }}>#</th>
                                                                    <th style={{ width: "18rem" }}>Patient Name</th>
                                                                    <th style={{ width: "10rem" }}>Case Number</th>
                                                                    <th style={{ width: "10rem" }}>Mobile Number</th>
                                                                    <th style={{ width: "7rem" }}></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {patientDetails.map((patient, index) => {
                                                                    return [
                                                                        <tr key={patient.patient_id}>
                                                                            <td>{index + 1}</td>
                                                                            <td className='text-left'>{`${salutation[patient.salutation]} ${patient.patient_name}`}</td>
                                                                            <td className='text-left'>{patient.patient_case_number}</td>
                                                                            <td className='text-left'>{patient.mobile_number}</td>
                                                                            <td><Button type='button' variant='info' size='sm' onClick={oEvent => onPressConfirmPatient(oEvent, patient)} >Confirm</Button></td>
                                                                        </tr>
                                                                    ]
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                        : <> </>}
                                                </div>
                                            </Row>
                                        </Modal.Body>
                                    </Modal>
                                </Col>
                            </Row>

                            <Row className={showDateFilter ? '' : 'd-none'}>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Date From</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filters.dateFrom}
                                            onChange={(oEvent) => handleFilterChange("dateFrom", oEvent.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Date From</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filters.dateTo}
                                            onChange={(oEvent) => handleFilterChange("dateTo", oEvent.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={4} className="mb-3 mt-4 pt-2">
                                    <Button variant="primary" className="me-2" onClick={onPressSearchInvoices}>Search</Button>
                                    <Button variant="outline-secondary" onClick={onPressReset}>Reset</Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Table Section */}
                    {patientInvoicesDetails.length > 0 ?
                        <>
                            {/* Export to Excel Button */}
                            <div className="mb-4 d-none">
                                <Button
                                    variant="success"
                                    size="sm"
                                    style={{ float: "right" }}
                                    className="mb-2"
                                    onClick={() =>
                                        exportToExcel(
                                            patientInvoicesDetails,
                                            "VTT_Patient_Invoices",
                                            [
                                                { label: "Invoice #", key: "invoice_number" },
                                                { label: "Patient", key: "patient_name" },
                                                { label: "Source", key: "source_type" },
                                                { label: "Date", key: "created_on" },
                                                { label: "Total", key: "total_amount" },
                                                { label: "Paid", key: "paid_amount" },
                                                { label: "Balance", key: "balance" },
                                            ],
                                            {
                                                "Search Query": filters.query,
                                                "Date From": filters.dateFrom,
                                                "Date To": filters.dateTo,
                                                "Exported On": new Date().toLocaleString(),
                                            },
                                            "Patient Invoice Report",
                                            userDetails.clinic_name
                                        )
                                    }
                                >
                                    Export to Excel
                                </Button>
                            </div>

                            <Table bordered hover responsive>
                                <thead light="true">
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice</th>
                                        <th>Source</th>
                                        <th>Date</th>
                                        {isDentist && <th className="text-end">Total</th>}
                                        <th className="text-end">Paid Till</th>
                                        <th className="text-end">Paid</th>
                                        <th className="text-end">Balance</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {patientInvoicesDetails.map((invoice, index) => {
                                        const balance = Number(invoice.balance || 0);
                                        const statusBadge =
                                            balance > 0 ? (
                                                <Badge bg="warning" text="dark">Part Payment</Badge>
                                            ) : (
                                                <Badge bg="success">Paid</Badge>
                                            );

                                        return (
                                            <tr key={invoice.invoice_id}>
                                                <td>{index + 1}</td>
                                                <td>{invoice.invoice_number}</td>
                                                <td>{sourceTypes[invoice.source_type]}</td>
                                                <td>{formatDisplayDate(invoice.invoice_date)}</td>
                                                <td className="text-end">₹{Number(invoice.total_amount || 0).toFixed(2)}</td>
                                                {isDentist && <td className="text-end">₹{Number(invoice.paid_till || 0).toFixed(2)}</td>}
                                                <td className="text-end">₹{Number(invoice.paid_amount || 0).toFixed(2)}</td>
                                                <td className="text-end">₹{Number(invoice.balance || 0).toFixed(2)}</td>
                                                <td>{statusBadge}</td>
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => onPressOpenModal(invoice)}
                                                        disabled={
                                                            fetchingPrint && selectedInvoice?.invoice_id === invoice.invoice_id
                                                        }
                                                    >
                                                        {fetchingPrint && selectedInvoice?.invoice_id === invoice.invoice_id ? "Loading..." : "Print"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            <CustomPagination
                                totalCount={totalCount}
                                currentPage={currentPage}
                                pageSize={pageSize}
                                onPageChange={setCurrentPage} />

                        </> : <> </>
                    }

                    {/* Print Modal */}
                    {selectedInvoice && printInvoiceData && (
                        <PrintInvoiceModal
                            show={showPrintModal}
                            onClose={onPressCloseModal}
                            invoiceData={printInvoiceData}
                            userDetails={userDetails}
                        />
                    )}

                </div>
            </Row>
            <ToastContainer />
            <BusyLoaderOverlay show={loading} />
        </>
    );
};

export default PatientInvoices;